import { z } from 'zod'

import { looksLikePhone, normalisePhone, PHONE_MESSAGE } from './phone'

export const enquiryTypes = ['residential', 'commercial'] as const

export const residentialServiceOptions = [
  'driveway',
  'excavation',
  'retaining',
  'rock-walls',
  'drainage',
  'landscaping',
  'paths',
  'steps',
  'other',
  'notsure',
] as const

export const commercialServiceOptions = [
  'site-prep',
  'subdivision-civil',
  'council-infrastructure',
  'emergency-storm',
  'grant-ready',
  'ongoing-partnership',
  'other',
] as const

export const serviceOptions = [...residentialServiceOptions, ...commercialServiceOptions] as const

export const timelineOptions = ['asap', '1-3months', '3plus'] as const

export type ResidentialServiceOption = (typeof residentialServiceOptions)[number]
export type CommercialServiceOption = (typeof commercialServiceOptions)[number]
export type ServiceOption = (typeof serviceOptions)[number]
export type TimelineOption = (typeof timelineOptions)[number]

const baseFields = {
  name: z
    .string()
    .min(1, 'Name is required.')
    .max(200, 'Name is too long.')
    .transform((v) => v.trim()),
  phone: z
    .string()
    .min(1, 'Phone number is required.')
    .max(30, 'Phone number is too long.')
    .transform(normalisePhone)
    .refine(looksLikePhone, PHONE_MESSAGE),
  postcode: z
    .string()
    .min(1, 'Postcode is required.')
    .max(10, 'Postcode is too long.')
    .transform((v) => v.trim()),
  // The job address picked from Google's suggestions, when the browser offered them. All optional:
  // the postcode above is still how a quote request is made.
  jobAddress: z
    .string()
    .max(200, 'Job address is too long.')
    .transform((v) => v.trim())
    .optional()
    .default(''),
  placeId: z.string().max(300).optional(),
  lat: z.number().min(-45).max(-9).optional(),
  lng: z.number().min(112).max(155).optional(),
  description: z
    .string()
    .max(2000, 'Description is too long.')
    .transform((v) => v.trim())
    .optional()
    .default(''),
}

export const residentialQuoteSchema = z.object({
  ...baseFields,
  enquiryType: z.literal('residential'),
  email: z.string().email('Please enter a valid email address.').optional().or(z.literal('')),
  service: z.enum(residentialServiceOptions, {
    message: 'Please select a service.',
  }),
})

export const commercialQuoteSchema = z.object({
  ...baseFields,
  enquiryType: z.literal('commercial'),
  email: z
    .string()
    .min(1, 'Email is required for commercial enquiries.')
    .email('Please enter a valid email address.'),
  service: z.enum(commercialServiceOptions, {
    message: 'Please select a service.',
  }),
  companyName: z
    .string()
    .max(200, 'Company name is too long.')
    .transform((v) => v.trim())
    .optional()
    .default(''),
  timeline: z.enum(timelineOptions, {
    message: 'Please select a timeline.',
  }),
})

export const quoteSchema = z.discriminatedUnion('enquiryType', [
  residentialQuoteSchema,
  commercialQuoteSchema,
])

export type QuoteFormData = z.input<typeof quoteSchema>
export type QuoteFormParsed = z.output<typeof quoteSchema>
export type ResidentialFormData = z.input<typeof residentialQuoteSchema>
export type CommercialFormData = z.input<typeof commercialQuoteSchema>
